/**
 * LabelingControlsPanel - Dynamic form controls for labeling feedback
 *
 * Provides interface for applying labels to feedback with dynamic fields
 * Requirement: ES-REQ-013, ES-REQ-014, ES-REQ-015, ES-REQ-018, ES-REQ-019, ES-REQ-057
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { useLabeling } from '@/context/labelingContext';
import { DynamicFieldControl } from './DynamicFieldControl';

const Container = styled.div`
  background-color: var(--bg-card);
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h3`
  margin: 0 0 20px 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-dark);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background-color: #ffebee;
  color: #c62828;
  border-radius: 4px;
  font-size: var(--font-size-base);
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-medium);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  ${props => props.$variant === 'primary' ? `
    background-color: var(--color-success-500);
    color: var(--text-inverse);

    &:hover:not(:disabled) {
      background-color: var(--color-success-400);
    }

    &:active:not(:disabled) {
      background-color: var(--color-success-300);
    }
  ` : `
    background-color: var(--bg-panel);
    color: var(--text-dark);

    &:hover:not(:disabled) {
      background-color: var(--color-neutral-200);
    }

    &:active:not(:disabled) {
      background-color: #d5d5d5;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const LabelingControlsPanel: React.FC = () => {
  const { state, submitLabel } = useLabeling();
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const currentFeedback = state.feedback[state.currentIndex];
  const hasFieldDefinitions = state.fieldDefinitions && state.fieldDefinitions.length > 0;

  // Check if all required fields have values
  const requiredFields = (state.fieldDefinitions || []).filter(f => f.required);
  const hasAllRequiredValues = requiredFields.every(fieldDef => {
    const value = formValues[fieldDef.name];
    return value !== '' && value !== null && value !== undefined;
  });

  // Also keep check for at least one value (for when no fields are marked required)
  const hasAnyValue = Object.values(formValues).some(value =>
    value !== '' && value !== null && value !== undefined
  );

  // Update canSubmit logic: require all required fields, or if none required, at least one value
  // Also disable if Sanity error occurred (ES-REQ-015-SANITY)
  const canSubmit = (requiredFields.length > 0 ? hasAllRequiredValues : hasAnyValue)
    && !state.isLoading
    && currentFeedback
    && hasFieldDefinitions
    && !state.sanityError;

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async () => {
    if (!canSubmit || !currentFeedback) {
      return;
    }

    try {
      await submitLabel({
        feedbackId: currentFeedback.feedbackId,
        recommendationId: currentFeedback.recommendationId,
        fields: formValues,
        sanitySnapshot: state.currentRecommendation || undefined
      });

      // Clear form after successful submission
      setFormValues({});
    } catch (error) {
      // Error is handled by context and displayed in StatusFeedback
      console.error('Submit failed:', error);
    }
  };

  const handleClear = () => {
    setFormValues({});
  };

  if (!currentFeedback) {
    return null;
  }

  return (
    <Container>
      <Title>Apply Label</Title>

      {!hasFieldDefinitions && (
        <ErrorMessage>
          Labels tab not configured with field metadata. Please ensure rows 1-2 contain field definitions.
        </ErrorMessage>
      )}

      {hasFieldDefinitions && state.fieldDefinitions.map(fieldDef => (
        <DynamicFieldControl
          key={fieldDef.name}
          fieldDef={fieldDef}
          value={formValues[fieldDef.name]}
          onChange={(value) => handleFieldChange(fieldDef.name, value)}
          disabled={state.isLoading}
        />
      ))}

      <ButtonGroup>
        <Button
          type="button"
          $variant="secondary"
          onClick={handleClear}
          disabled={state.isLoading || !hasFieldDefinitions}
        >
          Clear
        </Button>
        <Button
          type="button"
          $variant="primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {state.isLoading ? 'Submitting...' : 'Submit Label'}
        </Button>
      </ButtonGroup>
    </Container>
  );
};

export default LabelingControlsPanel;
